/**
 * Google Analytics 4.
 *
 * Two things about a single-page app make this more than pasting a snippet:
 *
 * 1. **gtag does not track route changes.** It records ONE page_view when the script loads and
 *    nothing thereafter, because there is no further page load. Left alone, every visit to
 *    /finance/supercars would be attributed to whatever page the visitor first landed on, and the
 *    six landing pages — the entire reason for adding analytics — would show no traffic at all.
 *    `usePageViews` sends a page_view on each navigation.
 *
 * 2. **The measurement ID is inlined at build time.** CRA replaces `process.env.REACT_APP_*` during
 *    the build, so changing it needs a rebuild, not just an env var edit in Vercel.
 *
 * Absent an ID this is inert — no script, no cookies, no requests. That is what makes a local dev
 * session or a preview deploy safe to run without polluting the production dataset.
 */

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

/**
 * Routes that are never recorded.
 *
 * Deliberately the same set `robots.txt` disallows: a page not worth indexing is not a page worth
 * measuring. These are sign-in, staff and account areas — none are marketing surfaces, and the
 * funnel itself is measured by the form-session pipeline rather than by GA.
 */
const NEVER_TRACKED = [
  '/auth', '/login', '/register',
  '/dashboard', '/dealer', '/admin', '/analytics', '/apply',
  // The funnel itself. Already measured far better by the form-session pipeline, which records
  // step-level drop-off and attributes it to a landing page; GA would duplicate that and put a
  // finance application's progress in a third party's dataset for no gain.
  '/get-started', '/quick-start',
  // Retired, and 301'd to /calculator at the edge — but a client-side visit still renders the
  // route for one tick before <Navigate> fires, and `usePageViews()` runs first. Without this, GA
  // would keep reporting sessions against a page that no longer exists.
  '/compare',
];

/**
 * A sign-in callback in progress, detected by the FRAGMENT rather than the path.
 *
 * This is the check that actually holds. The callback does not land on `/auth/callback` — `Login`
 * and `Register` send Google auth to `origin + '/dashboard'`, and `AppRouter` swaps in
 * `AuthCallback` based on the hash, whatever the path says. A path allow-list alone would miss the
 * real callback and report it. Wherever that fragment appears, nothing is tracked.
 */
function carriesAuthFragment() {
  if (typeof window === 'undefined') return false;
  return (window.location.hash || '').includes('session_id=');
}

/**
 * Honour Do Not Track.
 *
 * GA4 ignores DNT by default, so respecting it is a decision rather than a default — and the
 * privacy policy promises it. Checked at send time, so a setting change takes effect without a
 * reload.
 */
function doNotTrack() {
  if (typeof navigator === 'undefined') return false;
  const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
}

export const analyticsEnabled = () => Boolean(GA_MEASUREMENT_ID) && !doNotTrack();

/** True for a route whose URL cannot carry credentials and is worth recording. */
export function isTrackable(path) {
  if (carriesAuthFragment()) return false;
  return !NEVER_TRACKED.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * The URL to report, with the FRAGMENT removed.
 *
 * The fragment is where `#session_id=...` lives. The query string is kept deliberately: GA reads
 * `utm_*` from it for campaign attribution, which is most of the point of running ads at these
 * landing pages, and nothing on this site puts a credential in a query.
 */
function safeLocation() {
  if (typeof window === 'undefined') return '';
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}`;
}

let loaded = false;

/**
 * Load gtag.js once and record the first page.
 *
 * `send_page_view: false` on config, because the automatic one races the router: on a hard load of
 * /finance/jdm the script can fire before React has resolved the route, and the hit lands with the
 * wrong path. The first view is sent explicitly below instead, once we know where we are.
 */
export function initAnalytics() {
  if (loaded || !analyticsEnabled() || typeof window === 'undefined') return;
  loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // Must be `arguments`, not a rest parameter: gtag pushes the arguments OBJECT and GA reads it
  // positionally. A spread array is silently ignored.
  window.gtag = function gtag() { window.dataLayer.push(arguments); };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Record one page view. Path and title are passed explicitly so a SPA route is recorded correctly. */
export function trackPageView(path) {
  if (!analyticsEnabled() || typeof window === 'undefined' || !window.gtag) return;
  if (!isTrackable(path)) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: safeLocation(),
    page_title: document.title,
  });
}

/**
 * Send a page view on mount and on every navigation.
 *
 * Takes the path as an argument rather than importing the router, so this module stays free of
 * routing dependencies — the same reason `formSession` does it that way.
 */
export function usePageViews(React, path) {
  const { useEffect } = React;
  useEffect(() => {
    // Checked before LOADING, not just before sending: on an auth route the script should never be
    // fetched at all, so nothing is present that could report the URL.
    if (!isTrackable(path)) return;
    initAnalytics();
    trackPageView(path);
  }, [path]);
}
